// web/src/components/BoardSVG.tsx
import { useEffect, useMemo, useRef, useState } from 'react'
import type { RoomState } from '../lib/useRoom'
import Avatar from './Avatar'
import { shouldPlaySound } from './SoundToggle'
import { useViewport } from '../hooks/useViewport'

// Grace period countdown component
function GracePeriodCountdown({ opponentUsername, textY }: { opponentUsername?: string | null; textY: number }) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  useEffect(() => {
    // Get grace period end time from sessionStorage
    const gracePeriodEndStr = sessionStorage.getItem('tournament_grace_period_ends_at')
    if (!gracePeriodEndStr) {
      setTimeRemaining(null)
      return
    }

    const updateCountdown = () => {
      const now = new Date().getTime()
      const endTime = new Date(gracePeriodEndStr).getTime()
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000))
      setTimeRemaining(remaining)
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [])

  // Don't show if grace period has expired or no grace period set
  if (timeRemaining === null || timeRemaining <= 0) {
    return (
      <g fontFamily="system-ui, ui-sans-serif" textAnchor="middle" fill="#f4f4f5" stroke="#3e2615" strokeWidth={3} paintOrder="stroke">
        <text x={1590} y={textY} fontSize={28} fontWeight={600} opacity={0.7}>
          Waiting for opponent...
        </text>
      </g>
    )
  }

  // Format time as MM:SS
  const minutes = Math.floor(timeRemaining / 60)
  const seconds = timeRemaining % 60
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`

  return (
    <g fontFamily="system-ui, ui-sans-serif" textAnchor="middle" fill="#f4f4f5" stroke="#3e2615" strokeWidth={3} paintOrder="stroke">
      <text x={1590} y={textY - 20} fontSize={28} fontWeight={600} opacity={0.7}>
        Waiting for opponent...
      </text>
      <text x={1590} y={textY + 20} fontSize={36} fontWeight={700} fill="#ff6b6b" stroke="#3e2615" strokeWidth={4}>
        {timeStr}
      </text>
    </g>
  )
}

/** 16:9 global coordinates */
const VIEWBOX = { w: 1920, h: 1080 }
const IPAD_VIEWBOX = { w: 1920, h: 1600 }

/** Landmark coordinates tuned to your background - ORIGINAL DESKTOP VALUES */
const PLANK_Y = 800
const LANDMARKS = {
  // scale placement
  FULCRUM: { x: 960, y: PLANK_Y },
  PLANK_Y,


  // where played nuggets land on the plank (left/right "plates")
  // (closer to the ends per your note)
  PLATE_LEFT_X: 700,
  PLATE_RIGHT_X: 1240,

  // verticals
  PRIZE_Y: PLANK_Y - 22,  // center token/pile sits on the plank visually
  TOKEN_Y: PLANK_Y - 58,  // played nuggets sit above the plank

  // clickable tray
  TRAY_Y: 960,
  TRAY_SPAN: 1000,

  // sign anchors (moved inwards & lower)
  SIGN_LEFT:  { x: 440,  y: 610 },
  SIGN_RIGHT: { x: 1480, y: 610 },

  // piles under signs (lower)
  PILE_LEFT:  { x: 310,  y: 820 },
  PILE_RIGHT: { x: 1610, y: 820 },

  // Clock graphic placement (bottom-left, moved inward 150px)
  CLOCK_FACE: { x: 400, y: 1070 },
}

// Clock image and text positioning offsets (adjustable in one place)
const CLOCK = {
  size: 340,          // image box (px)
  imgDx: 6,           // nudge the PNG right (+ = move right)
  imgDy: -10,         // nudge the PNG up (+ = move down)
  textDx: 5,          // nudge the number (+ = right)
  textDy: 50,         // nudge the number (+ = down)
}

/** PNG intrinsic sizes; scaled in SVG-space */
const ART = {
  plank: { w: 1040, h: 90 },   // /scale_plank.png
  base:  { w: 210,  h: 180 },  // /scale_base.png
  nuggetBaseSize: 112,         // base for 1-lb
}

/* ===================== helpers & styling ===================== */

const TOKEN_NUM_FILL = '#3e2615'
const TOKEN_NUM_STROKE = '#f7e7b2'
const TOKEN_NUM_STROKE_WIDTH = 2
const TOKEN_NUM_STROKE_OPACITY = 0.55

function formatSeconds(total: number) {
  const clamped = Math.max(0, total)
  const minutes = Math.floor(clamped / 60)
  const seconds = clamped % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function TokenNumber({ n, fontSize }: { n: number; fontSize: number }) {
  return (
    <text
      x={0}
      y={2}
      textAnchor="middle"
      dominantBaseline="middle"
      fontWeight={900}
      fontSize={fontSize}
      fill={TOKEN_NUM_FILL}
      stroke={TOKEN_NUM_STROKE}
      strokeWidth={TOKEN_NUM_STROKE_WIDTH}
      style={{ paintOrder: 'stroke', strokeOpacity: TOKEN_NUM_STROKE_OPACITY }}
    >
      {n}
    </text>
  )
}

function nuggetSize(n: number) {
  const base = ART.nuggetBaseSize
  const step = 12
  const px = base + (n - 1) * step
  return { w: px, h: px }
}

function CenteredImage({
  href, cx, cy, w, h,
}: { href: string; cx: number; cy: number; w: number; h: number }) {
  return (
    <image
      href={href}
      x={cx - w / 2}
      y={cy - h / 2}
      width={w}
      height={h}
      preserveAspectRatio="none"
    />
  )
}

function Nugget({ cx, cy, n, showNumber = true, fixedSize = false }: {
  cx: number;
  cy: number;
  n: number;
  showNumber?: boolean;
  fixedSize?: boolean
}) {
  const { w, h } = fixedSize ? nuggetSize(4) : nuggetSize(n)  // Always use 4lb size for fixedSize (hides opponent's value)
  const labelSize = Math.round(Math.min(w, h) * 0.33)
  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <image href="./nugget.png" x={-w / 2} y={-h / 2} width={w} height={h} preserveAspectRatio="xMidYMid meet" />
      {showNumber && <TokenNumber n={n} fontSize={labelSize} />}
    </g>
  )
}

function readScores(s: any): { you: number; opp: number } {
  if (s?.scores && typeof s.scores.you === 'number' && typeof s.scores.opp === 'number') {
    return { you: s.scores.you, opp: s.scores.opp }
  }
  const you = s?.youScore ?? s?.you_score ?? s?.you ?? 0
  const opp = s?.oppScore ?? s?.opp_score ?? s?.opp ?? 0
  return { you: Number(you) || 0, opp: Number(opp) || 0 }
}

/** consistent tilt: ±8°, ties 0° */
function tiltAngle(last: any): number {
  if (!last) return 0
  if (last?.outcome === 'you') return -8
  if (last?.outcome === 'opp') return 8
  return 0
}

/** pile art by count (0..∞) */
function pileFor(k: number): string | null {
  if (k <= 0) return null
  if (k === 1) return './nugget_pile3.png'
  if (k === 2) return './nugget_pile6.png'
  return './nugget_pile9.png'
}

/* ===================== component ===================== */

export default function BoardSVG({
  s,
  onPlay,
  onNextRound,                     // ⬅️ add this
  backgroundHref = './goldrush_background.png',
  playerUsername = 'You',
  opponentUsername = 'Opponent',
  isFullscreen = false,
}: {
  s: RoomState
  onPlay: (n: number) => void
  onNextRound?: () => void         // ⬅️ add this
  backgroundHref?: string
  playerUsername?: string
  opponentUsername?: string
  isFullscreen?: boolean
}) {

  const viewport = useViewport({ mobileBreakpoint: 1024, treatPortraitAsMobile: true })
  const { isMobile, isPortrait, width, height } = viewport
  const isAndroidApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android'
  const userAgent = typeof navigator !== 'undefined'
    ? (navigator.userAgent || navigator.vendor || ((window as any).opera as string | undefined) || '').toLowerCase()
    : ''
  const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0
  const isIpadUA = userAgent.includes('ipad') || (userAgent.includes('macintosh') && maxTouchPoints > 2)
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios'
  
  // Bulletproof iPad detection - exclude iPhone
  const isIOSPad = (() => {
    if (isIpadUA) return true;
    if (isIOSApp && Math.max(width, height) >= 768) {
      // Additional check: if it's iPhone (not iPad), exclude it
      if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return false;
      if (userAgent.includes('ipod')) return false;
      return true;
    }
    return false;
  })();
  
  // Bulletproof iPhone detection
  const isIPhone = (() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (isIOSPad) return false;
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    if (userAgent.includes('ipod')) return true;
    if (isIOSApp && !isIOSPad) {
      const maxDim = Math.max(width, height);
      const minDim = Math.min(width, height);
      if (maxDim < 768 || minDim < 768) return true;
    }
    return false;
  })();
  
  const isIpadLayout = isIOSPad
  const viewBox = isIOSPad ? IPAD_VIEWBOX : VIEWBOX
  const preserveAspect = isFullscreen ? 'xMidYMid meet' : 'xMidYMid slice'
  const backgroundPreserve = preserveAspect
  // iPhone uses regular background (same as Android), iPad uses tablet background
  const effectiveBackgroundHref = isIOSPad ? './goldrush_background-1920x1600.png' : backgroundHref

  const baseL = LANDMARKS
  const basePlankHeight = ART.plank.h
  let targetPlankHeight = basePlankHeight
  let plankCenterY = baseL.PLANK_Y
  let plankShift = 0
  let L = baseL
  const baseAvatarY = 60
  const baseTextY = 285
  let avatarY = baseAvatarY  // Original desktop avatar Y position
  let textY = baseTextY   // Original desktop text Y position
  let trayY = baseL.TRAY_Y
  let pileLeftY = baseL.PILE_LEFT.y
  let pileRightY = baseL.PILE_RIGHT.y
  let clockY = baseL.CLOCK_FACE.y

  if (isMobile) {
    // Mobile adjustments: move plank up, avatars down, nuggets up, clock up
    targetPlankHeight = Math.max(basePlankHeight - 10, 60)

    // Set plank to previous working position: 760 (40px up from desktop 800)
    plankCenterY = 760
    plankShift = plankCenterY - baseL.PLANK_Y  // 760 - 800 = -40

    // Scale desktop values proportionally - use same offsets from plank
    L = {
      ...baseL,
      PLANK_Y: plankCenterY,  // 760
      PRIZE_Y: plankCenterY - 22,  // 760 - 22 = 738 (same formula as desktop)
      TOKEN_Y: plankCenterY - 58,  // 760 - 58 = 702 (same formula as desktop)
      FULCRUM: { ...baseL.FULCRUM, y: baseL.FULCRUM.y + plankShift },
    }
    // Mobile-specific position adjustments
    if (isPortrait) {
      // Portrait mode adjustments
      avatarY = 120   // Move avatars down 60px on mobile
      textY = 345     // Move text down 60px on mobile
      trayY = 900     // Previous working value: moved up 60px from 960
      pileLeftY = 760 // Move piles up 60px on mobile
      pileRightY = 760 // Move piles up 60px on mobile
      clockY = 1020   // Move clock up 50px on mobile
    } else {
      // Landscape mode adjustments - move avatars down more to clear status bar, clock up more to prevent cutoff
      avatarY = 170   // Move avatars down 110px on mobile landscape (was 150) to clear status bar
      textY = 395     // Maintain same relative position to avatar (170 + 225px difference = 395)
      trayY = 900     // Previous working value: moved up 60px from 960
      pileLeftY = 760 // Move piles up 60px on mobile
      pileRightY = 760 // Move piles up 60px on mobile
      clockY = 1020   // Same as portrait mode - clock position unchanged
    }
  }

  // iPad-only: scale vertical layout to match taller 1920x1600 background without letterboxing
  if (isIpadLayout) {
    const scaleY = IPAD_VIEWBOX.h / VIEWBOX.h
    const scaleVal = (v: number) => Math.round(v * scaleY)
    const offsetPrize = Math.round(22 * scaleY)
    const offsetToken = Math.round(58 * scaleY)
    const scaleLift = 60    // move scale up to clear tray nuggets (lighter lift)
    const signLift = 30     // raise scores toward signs, but keep closer to piles
    const avatarDown = 60   // bring avatar down slightly
    const usernameUp = 35   // bring username up slightly (leave some padding)

    const scaledTrayY = scaleVal(trayY)
    const scaledPileLeftY = scaleVal(pileLeftY)
    const scaledPileRightY = scaleVal(pileRightY)
    const scaledClockY = Math.min(scaleVal(clockY), IPAD_VIEWBOX.h - 40)

    const scaledPlankCenter = scaleVal(plankCenterY) - scaleLift
    const scaledFulcrumY = scaleVal(L.FULCRUM.y) - scaleLift

    L = {
      ...L,
      PLANK_Y: scaledPlankCenter,
      PRIZE_Y: scaledPlankCenter - offsetPrize,
      TOKEN_Y: scaledPlankCenter - offsetToken,
      TRAY_Y: scaledTrayY,
      FULCRUM: { ...L.FULCRUM, y: scaledFulcrumY },
      SIGN_LEFT: { ...L.SIGN_LEFT, y: scaleVal(L.SIGN_LEFT.y) - signLift },
      SIGN_RIGHT: { ...L.SIGN_RIGHT, y: scaleVal(L.SIGN_RIGHT.y) - signLift },
      PILE_LEFT: { ...L.PILE_LEFT, y: scaledPileLeftY },
      PILE_RIGHT: { ...L.PILE_RIGHT, y: scaledPileRightY },
      CLOCK_FACE: { ...L.CLOCK_FACE, y: scaledClockY },
    }

    avatarY = scaleVal(avatarY) + avatarDown
    textY = scaleVal(textY) - usernameUp
    trayY = scaledTrayY
    pileLeftY = scaledPileLeftY
    pileRightY = scaledPileRightY
    clockY = scaledClockY
    plankCenterY = scaledPlankCenter
  }

  // --- Sound: Web Audio API for Android & iOS, HTML5 Audio for web only ---
  const audioContextRef = useRef<AudioContext | null>(null)
  const soundBuffersRef = useRef<Map<string, AudioBuffer>>(new Map())
  const soundBuffersLoadingRef = useRef<Set<string>>(new Set())
  
  // Android & iOS: Web Audio API sources for clock and background song
  const clockSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const beepsSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const backgroundSongSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const clockGainRef = useRef<GainNode | null>(null)
  const backgroundSongGainRef = useRef<GainNode | null>(null)
  const clockStartTimeRef = useRef<number>(0)
  const backgroundSongStartTimeRef = useRef<number>(0)
  
  // Detect if we should use Web Audio API (native apps) or HTML5 Audio (web browsers)
  const useWebAudioAPI = isAndroidApp || isIOSApp
  
  // HTML5 Audio elements for web only
  const chooseSoundRef = useRef<HTMLAudioElement | null>(null)
  const prizeSoundRef = useRef<HTMLAudioElement | null>(null)
  const dropSoundRef = useRef<HTMLAudioElement | null>(null)
  const tiltSlideSoundRef = useRef<HTMLAudioElement | null>(null)
  const tieSoundRef = useRef<HTMLAudioElement | null>(null)
  const roundClockSoundRef = useRef<HTMLAudioElement | null>(null)
  const beepsSoundRef = useRef<HTMLAudioElement | null>(null)
  const backgroundSongRef = useRef<HTMLAudioElement | null>(null)
  const backgroundSongStartedRef = useRef<boolean>(false)
  const backgroundSongResumeTimeRef = useRef<number>(0)
  const roundClockResumeTimeRef = useRef<number>(0)
  const beepsActiveRef = useRef<boolean>(false)
  const beepsRoundRef = useRef<number | null>(null)
  const clockSkewRef = useRef<number>(0)

  // Initialize audio: Web Audio API for Android & iOS, HTML5 Audio for web only
  useEffect(() => {
    // Android & iOS: Create AudioContext (single context for all sounds - better resource management)
    let audioContext: AudioContext | null = null
    if (useWebAudioAPI) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
        if (AudioContextClass) {
          audioContext = new AudioContextClass()
          audioContextRef.current = audioContext
        }
      } catch (err) {
        console.error('[Audio] Failed to create AudioContext:', err)
      }
    }

    // Android & iOS: Load sound effect buffers via Web Audio API
    const loadSoundBuffer = async (name: string, src: string) => {
      if (!audioContext || soundBuffersRef.current.has(name) || soundBuffersLoadingRef.current.has(name)) {
        return
      }
      
      soundBuffersLoadingRef.current.add(name)
      try {
        const response = await fetch(src)
        const arrayBuffer = await response.arrayBuffer()
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        soundBuffersRef.current.set(name, audioBuffer)
        
        // Start background song as soon as it's loaded (if game is ready)
        if (name === 'background' && useWebAudioAPI && !backgroundSongSourceRef.current) {
          const gameReady = s && (s.round >= 1 || s.ui_phase === 'decide')
          if (gameReady && !s?.finished && shouldPlaySound()) {
            startBackgroundSong()
          }
        }
      } catch (err) {
        console.error(`[Audio] Failed to load ${name}:`, err)
      } finally {
        soundBuffersLoadingRef.current.delete(name)
      }
    }

    // Android & iOS: Load all sound effects, clock, and background song via Web Audio API
    if (audioContext) {
      loadSoundBuffer('choose', './ChooseNugget.mp3')
      loadSoundBuffer('prize', './PrizeNuggetOnScale.mp3')
      loadSoundBuffer('drop', './FloatingNuggetDrop.mp3')
      loadSoundBuffer('tilt', './TiltSlide.mp3')
      loadSoundBuffer('tie', './TieRound.mp3')
      loadSoundBuffer('clock', './RoundClock.mp3')
      loadSoundBuffer('beeps', './Beeps.mp3')
      loadSoundBuffer('background', './Song.mp3')
    }

    // Web only: Create HTML5 Audio elements for sound effects (Android & iOS use Web Audio API above)
    if (!useWebAudioAPI) {
      const attachAutoRebuild = (src: string, ref: React.RefObject<HTMLAudioElement>) => {
        const build = () => {
          const audio = new Audio(src)
          audio.preload = 'auto'
          audio.load()
          const handleError = (e: Event) => {
            console.error('[Audio] Error loading:', src, audio.error)
            const fresh = build()
            ref.current = fresh
          }
          audio.addEventListener('error', handleError)
          return audio
        }
        const audio = build()
        ref.current = audio
        return audio
      }

      attachAutoRebuild('./ChooseNugget.mp3', chooseSoundRef)
      attachAutoRebuild('./PrizeNuggetOnScale.mp3', prizeSoundRef)
      attachAutoRebuild('./FloatingNuggetDrop.mp3', dropSoundRef)
      attachAutoRebuild('./TiltSlide.mp3', tiltSlideSoundRef)
      attachAutoRebuild('./TieRound.mp3', tieSoundRef)
      attachAutoRebuild('./Beeps.mp3', beepsSoundRef)
    }

    // Initialize clock and background song
    // Android & iOS: Will use Web Audio API (buffers loaded above)
    // Web only: Use HTML5 Audio elements
    if (!useWebAudioAPI) {
      const roundClock = new Audio('./RoundClock.mp3')
      roundClock.preload = 'auto'
      roundClock.addEventListener('error', (e) => {
        console.error('[Audio] RoundClock error:', e, roundClock.error, { src: roundClock.src })
      })
      const updateClockResume = () => {
        try { roundClockResumeTimeRef.current = roundClock.currentTime } catch {}
      }
      roundClock.addEventListener('timeupdate', updateClockResume)
      roundClock.addEventListener('pause', updateClockResume)
      roundClockSoundRef.current = roundClock

      const backgroundSong = new Audio('./Song.mp3')
      backgroundSong.preload = 'auto'
      backgroundSong.loop = true
      backgroundSong.volume = 1.0
      // Track playback position so we can restore after suspends
      const updateResumeTime = () => {
        try { backgroundSongResumeTimeRef.current = backgroundSong.currentTime } catch {}
      }
      backgroundSong.addEventListener('timeupdate', updateResumeTime)
      backgroundSong.addEventListener('pause', updateResumeTime)
      backgroundSongRef.current = backgroundSong
    }

    return () => {
      // Cleanup Web Audio API (Android & iOS)
      if (audioContext) {
        audioContext.close().catch(() => {})
        audioContextRef.current = null
      }
      soundBuffersRef.current.clear()
      soundBuffersLoadingRef.current.clear()
      
      // Android & iOS: Stop Web Audio API sources
      if (useWebAudioAPI && audioContext) {
        try {
          if (clockSourceRef.current) {
            clockSourceRef.current.stop()
            clockSourceRef.current.disconnect()
            clockSourceRef.current = null
          }
          if (beepsSourceRef.current) {
            beepsSourceRef.current.stop()
            beepsSourceRef.current.disconnect()
            beepsSourceRef.current = null
          }
          if (backgroundSongSourceRef.current) {
            backgroundSongSourceRef.current.stop()
            backgroundSongSourceRef.current.disconnect()
            backgroundSongSourceRef.current = null
          }
          if (clockGainRef.current) {
            clockGainRef.current.disconnect()
            clockGainRef.current = null
          }
          if (backgroundSongGainRef.current) {
            backgroundSongGainRef.current.disconnect()
            backgroundSongGainRef.current = null
          }
        } catch {}
      }
      
      // Web only: Cleanup HTML5 Audio elements
      if (!useWebAudioAPI) {
        try { chooseSoundRef.current?.pause() } catch {}
        chooseSoundRef.current = null
        try { prizeSoundRef.current?.pause() } catch {}
        prizeSoundRef.current = null
        try { dropSoundRef.current?.pause() } catch {}
        dropSoundRef.current = null
        try { tiltSlideSoundRef.current?.pause() } catch {}
        tiltSlideSoundRef.current = null
        try { tieSoundRef.current?.pause() } catch {}
        tieSoundRef.current = null
        try {
          if (roundClockSoundRef.current) {
            roundClockSoundRef.current.pause()
            roundClockSoundRef.current = null
          }
        } catch {}
        try {
          if (beepsSoundRef.current) {
            beepsSoundRef.current.pause()
            beepsSoundRef.current = null
          }
        } catch {}
        const bg = backgroundSongRef.current
        if (bg) {
          bg.removeEventListener('timeupdate', () => {})
          bg.removeEventListener('pause', () => {})
          try {
            bg.pause()
            bg.currentTime = 0
          } catch {}
          backgroundSongRef.current = null
        }
      }
      backgroundSongStartedRef.current = false
    }
  }, [useWebAudioAPI])

  // Start background music when gameboard is ready (all platforms)
  useEffect(() => {
    // Don't start if game is finished
    if (s?.finished) return
    // Start when game has begun (round >= 1) or when in decide phase
    // This ensures it starts as soon as gameplay begins, not waiting for first prize
    const gameReady = s && (s.round >= 1 || s.ui_phase === 'decide')
    if (!gameReady) return
    
    // Only start once per game session - check both ref and actual state
    if (backgroundSongStartedRef.current) {
      // If already started but paused, don't restart - let visibility handler deal with it
      const bg = backgroundSongRef.current
      if (bg && !bg.paused && bg.currentTime > 0) {
        return // Already playing, definitely don't restart
      }
      return // Already marked as started, don't restart
    }
    
    backgroundSongStartedRef.current = true
    startBackgroundSong()
  }, [s?.finished]) // Only re-run if game finishes - not on every round/phase

  // Play sound effect: Web Audio API on Android & iOS, HTML5 Audio on web only
  const playSoundEffect = (name: string) => {
    if (!shouldPlaySound()) return
    
    // Android & iOS: Use Web Audio API
    if (useWebAudioAPI) {
      const audioContext = audioContextRef.current
      if (!audioContext) {
        console.warn(`[Sound] AudioContext not available, cannot play ${name}`)
        return
      }

      const buffer = soundBuffersRef.current.get(name)
      if (!buffer) {
        // Buffer not loaded yet - wait for it and retry (important for first prize)
        if (soundBuffersLoadingRef.current.has(name)) {
          // Still loading, wait a bit and retry
          setTimeout(() => {
            const loadedBuffer = soundBuffersRef.current.get(name)
            if (loadedBuffer) {
              try {
                if (audioContext.state === 'suspended') {
                  audioContext.resume().catch(() => {})
                }
                const source = audioContext.createBufferSource()
                source.buffer = loadedBuffer
                source.connect(audioContext.destination)
                source.start(0)
              } catch (err) {
                console.error(`[Sound] Failed to play ${name} after load:`, err)
              }
            }
          }, 200)
        }
        return
      }

      try {
        // Resume AudioContext if suspended (required on some browsers/Android)
        // This is critical for the first sound to play
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            // After resuming, create and play the source
            try {
              const source = audioContext.createBufferSource()
              source.buffer = buffer
              source.connect(audioContext.destination)
              source.start(0)
            } catch (err) {
              console.error(`[Sound] Failed to play ${name} after resume:`, err)
            }
          }).catch(() => {})
          return // Will play after resume
        }

        // AudioContext is already running, play immediately
        const source = audioContext.createBufferSource()
        source.buffer = buffer
        source.connect(audioContext.destination)
        source.start(0)
      } catch (err) {
        console.error(`[Sound] Failed to play ${name}:`, err)
      }
      return
    }

    // Web: Use HTML5 Audio
    const getRef = (): HTMLAudioElement | null => {
      switch (name) {
        case 'choose': return chooseSoundRef.current
        case 'prize': return prizeSoundRef.current
        case 'drop': return dropSoundRef.current
        case 'tilt': return tiltSlideSoundRef.current
        case 'tie': return tieSoundRef.current
        default: return null
      }
    }

    const audio = getRef()
    if (!audio) {
      console.warn(`[Sound] ${name} ref is null`)
      return
    }

    const doPlay = () => {
      try {
        audio.currentTime = 0
        audio.play().catch(err => {
          console.error(`[Sound] ${name} play() failed:`, err)
        })
      } catch (err) {
        console.error(`[Sound] ${name} play() exception:`, err)
      }
    }

    if (audio.readyState >= 2) {
      doPlay()
    } else {
      audio.load()
      audio.addEventListener('canplay', () => doPlay(), { once: true })
    }
  }

  const playChooseSound = () => playSoundEffect('choose')
  const playPrizeSound = () => playSoundEffect('prize')
  const playDropSound = () => playSoundEffect('drop')
  const playTiltSlideSound = () => playSoundEffect('tilt')
  const playTieSound = () => playSoundEffect('tie')

  const stopRoundClockAudio = () => {
    if (useWebAudioAPI) {
      if (clockSourceRef.current) {
        try {
          clockSourceRef.current.stop()
          clockSourceRef.current.disconnect()
        } catch {}
        clockSourceRef.current = null
      }
    } else {
      const clockSound = roundClockSoundRef.current
      if (clockSound) {
        try { clockSound.pause(); clockSound.currentTime = 0 } catch {}
      }
    }
  }

  const stopBeepsAudio = () => {
    if (useWebAudioAPI) {
      if (beepsSourceRef.current) {
        try {
          beepsSourceRef.current.stop()
          beepsSourceRef.current.disconnect()
        } catch {}
        beepsSourceRef.current = null
      }
    } else {
      const beeps = beepsSoundRef.current
      if (beeps) {
        try { beeps.pause(); beeps.currentTime = 0 } catch {}
      }
    }
    beepsActiveRef.current = false
  }

  const stopClockAudio = () => {
    stopRoundClockAudio()
    stopBeepsAudio()
  }

  const playBeeps = (reason: string) => {
    if (!shouldPlaySound()) return
    if (beepsActiveRef.current) return

    beepsActiveRef.current = true

    if (useWebAudioAPI) {
      const audioContext = audioContextRef.current
      const buffer = soundBuffersRef.current.get('beeps')
      if (!audioContext || !buffer) {
        beepsActiveRef.current = false
        return
      }

      if (beepsSourceRef.current) {
        try {
          beepsSourceRef.current.stop()
          beepsSourceRef.current.disconnect()
        } catch {}
        beepsSourceRef.current = null
      }

      try {
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }
        const source = audioContext.createBufferSource()
        source.buffer = buffer
        source.loop = false
        source.connect(audioContext.destination)
        source.start(audioContext.currentTime)
        beepsSourceRef.current = source
        source.addEventListener('ended', () => {
          if (beepsSourceRef.current === source) {
            beepsSourceRef.current = null
          }
          beepsActiveRef.current = false
        })
      } catch (err) {
        console.error('[Beeps] Web Audio API play failed:', err, { reason })
        beepsActiveRef.current = false
      }
      return
    }

    const beeps = beepsSoundRef.current
    if (!beeps) {
      beepsActiveRef.current = false
      return
    }

    const handleEnded = () => {
      beepsActiveRef.current = false
    }
    beeps.addEventListener('ended', handleEnded, { once: true })

    try {
      beeps.currentTime = 0
    } catch {}

    const p = beeps.play()
    if (p && typeof (p as any).catch === 'function') {
      p.catch(err => {
        console.error('[Beeps] play() failed:', err, { reason })
        beepsActiveRef.current = false
      })
    }
  }

  const playRoundClock = (reason: string, startOffset: number = 0) => {
    if (!shouldPlaySound()) return
    if (beepsActiveRef.current) return
    if (beepsRoundRef.current === s.round) return
    
    // Android & iOS: Use Web Audio API
    if (useWebAudioAPI) {
      const audioContext = audioContextRef.current
      const buffer = soundBuffersRef.current.get('clock')
      if (!audioContext || !buffer) {
        // Not ready yet
        return
      }

      // Stop existing source if playing
      if (clockSourceRef.current) {
        try {
          clockSourceRef.current.stop()
          clockSourceRef.current.disconnect()
        } catch {}
        clockSourceRef.current = null
      }

      try {
        // Resume AudioContext if suspended
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }

        // Create new source and play
        const source = audioContext.createBufferSource()
        const gain = audioContext.createGain()
        source.buffer = buffer
        source.loop = false // play once per round for proper timing
        source.connect(gain)
        gain.connect(audioContext.destination)
        
        // Start at the correct offset to sync with elapsed time
        const startTime = audioContext.currentTime
        const bufferDuration = buffer.duration
        // Calculate offset within the buffer (wrap around if needed)
        let offsetSeconds = 0
        if (startOffset > 0 && bufferDuration > 0) {
          offsetSeconds = startOffset % bufferDuration
        }
        
        source.start(startTime, offsetSeconds)
        clockStartTimeRef.current = startTime - offsetSeconds // Track the virtual start time
        clockSourceRef.current = source
        clockGainRef.current = gain
        
        // Handle end (shouldn't happen with loop=true, but just in case)
        source.addEventListener('ended', () => {
          if (clockSourceRef.current === source) {
            clockSourceRef.current = null
          }
        })
      } catch (err) {
        console.error('[Clock] Web Audio API play failed:', err)
      }
      return
    }

    // Web: Use HTML5 Audio
    const clock = roundClockSoundRef.current
    if (!clock) {
      console.warn('[Clock] ref is null')
      return
    }

    const now = Date.now()
    if (!clock.paused && now - lastClockNudgeRef.current < 400) return
    lastClockNudgeRef.current = now

    // Set currentTime to sync with elapsed time
    try {
      if (clock.readyState >= 2) {
        if (startOffset > 0 && clock.duration) {
          // Sync to elapsed time (wrap around if needed)
          clock.currentTime = (startOffset % clock.duration)
        } else if (clock.paused) {
          clock.currentTime = 0
        }
      }
    } catch (err) {
      console.warn('[Clock] currentTime reset failed:', err, {
        readyState: clock.readyState,
        src: clock.src,
        duration: clock.duration
      })
    }

    const p = clock.play()
    if (p && typeof (p as any).catch === 'function') {
      p.catch(err => {
        console.error('[Clock] play() failed:', err, {
          reason,
          readyState: clock.readyState,
          paused: clock.paused,
          ended: clock.ended,
          currentTime: clock.currentTime,
          src: clock.src,
          error: clock.error
        })
      })
    }
  }

  const playRoundClockWithRetry = (reason: string, round: number, uiPhase: string, prize: number | null, elapsedSeconds: number = 0) => {
    // Don't start if game is finished
    if (s.finished) return
    if (beepsActiveRef.current) return
    if (beepsRoundRef.current === s.round) return
    
    // Android & iOS: Check if already playing before starting
    if (useWebAudioAPI) {
      if (clockSourceRef.current) {
        // Already playing, don't start again
        return
      }
    }
    
    playRoundClock(reason, elapsedSeconds)

    // Retry once shortly after if it didn't start (Android & iOS can be flaky)
    window.setTimeout(() => {
      // Don't retry if game is finished
      if (s.finished) return
      
      // Android & iOS: Check Web Audio API source - only retry if still not playing
      if (useWebAudioAPI) {
        if (!clockSourceRef.current && shouldPlaySound() && 
            uiPhase === 'decide' && typeof prize === 'number') {
          if (Date.now() - lastClockNudgeRef.current >= 250) {
            playRoundClock(reason + ':retry', elapsedSeconds)
          }
        }
        return
      }
      
      // Web: Check HTML5 Audio
      const clock = roundClockSoundRef.current
      if (!clock) return
      // Only retry if still in a state where clock should be playing
      if (shouldPlaySound() && clock.paused && !clock.ended && 
          uiPhase === 'decide' && typeof prize === 'number') {
        // Prevent a double start if we already nudged recently
        if (Date.now() - lastClockNudgeRef.current >= 250) {
          playRoundClock(reason + ':retry', elapsedSeconds)
        }
      }
    }, 150)
  }

  // Guard against the clock stalling mid-round
  const lastClockNudgeRef = useRef<number>(0)
  useEffect(() => {
    if (s.finished || s.ui_phase !== 'decide' || typeof s.prize !== 'number') {
      // Stop clock immediately when game finishes - don't restart
      if (s.finished) {
        stopClockAudio()
      }
      return
    }
    const tick = () => {
      if (!shouldPlaySound() || s.finished) return // Don't start if game is finished
      if (beepsActiveRef.current) return
      if (beepsRoundRef.current === s.round) return
      
      // Android & iOS: Check Web Audio API source
      if (useWebAudioAPI) {
        const audioContext = audioContextRef.current
        const buffer = soundBuffersRef.current.get('clock')
        if (!audioContext || !buffer) return
        
        // If source stopped, restart it at correct position
        if (!clockSourceRef.current) {
          const now = Date.now()
          if (now - lastClockNudgeRef.current < 800) return
          if (!s.decisionDeadline || !s.decisionSeconds) return
          
          // Calculate elapsed time to sync the sound
          const roundStart = s.decisionDeadline - s.decisionSeconds * 1000
          const elapsedSeconds = Math.max(0, (now - roundStart) / 1000)
          
          try {
            if (audioContext.state === 'suspended') {
              audioContext.resume().catch(() => {})
            }
            const source = audioContext.createBufferSource()
            const gain = audioContext.createGain()
            source.buffer = buffer
            source.loop = false // play once per round for proper timing
            source.connect(gain)
            gain.connect(audioContext.destination)
            
            // Start at the correct offset to sync with elapsed time
            const startTime = audioContext.currentTime
            const bufferDuration = buffer.duration
            const offsetSeconds = elapsedSeconds > 0 && bufferDuration > 0
              ? elapsedSeconds % bufferDuration
              : 0
            
            source.start(startTime, offsetSeconds)
            clockStartTimeRef.current = startTime - offsetSeconds
            clockSourceRef.current = source
            clockGainRef.current = gain
            lastClockNudgeRef.current = now
          } catch (err) {
            console.error('[Clock] Web Audio API restart failed:', err)
          }
        }
        return
      }
      
      // Web: Check HTML5 Audio
      const clock = roundClockSoundRef.current
      if (!clock) return
      // Check if clock should be playing but isn't
      if (clock.paused || clock.ended) {
        if (clock.readyState < 2) {
          // Not ready yet, try loading
          try { clock.load() } catch {}
          return
        }
        const now = Date.now()
        if (now - lastClockNudgeRef.current < 800) return
        if (!s.decisionDeadline || !s.decisionSeconds) return
        const roundStart = s.decisionDeadline - s.decisionSeconds * 1000
        const elapsed = Math.max(0, (now - roundStart) / 1000)
        if (clock.duration && elapsed > clock.duration - 0.05) return
        try {
          const target = clock.duration
            ? Math.min(clock.duration - 0.2, Math.max(0, elapsed))
            : Math.max(0, elapsed)
          clock.currentTime = target
        } catch (err) {
          console.warn('[Clock] Failed to set currentTime:', err)
        }
        try {
          clock.play().catch(err => {
            console.error('[Clock] Tick play() failed:', err, { readyState: clock.readyState, src: clock.src, error: clock.error })
          })
          lastClockNudgeRef.current = now
        } catch (err) {
          console.error('[Clock] Tick play() exception:', err)
        }
      }
    }
    const id = window.setInterval(tick, 300)
    return () => window.clearInterval(id)
  }, [s.finished, s.ui_phase, s.prize, s.decisionDeadline, s.decisionSeconds])

  const startBackgroundSong = () => {
    if (!shouldPlaySound()) return
    
    // Android & iOS: Use Web Audio API
    if (useWebAudioAPI) {
      const audioContext = audioContextRef.current
      const buffer = soundBuffersRef.current.get('background')
      if (!audioContext || !buffer) {
        // Not ready yet
        return
      }
      
      // Don't restart if already playing
      if (backgroundSongSourceRef.current) {
        return
      }
      
      try {
        // Resume AudioContext if suspended
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }
        
        // Create looping source
        const source = audioContext.createBufferSource()
        const gain = audioContext.createGain()
        source.buffer = buffer
        source.loop = true
        gain.gain.value = 0.75 // 75% volume on Android
        source.connect(gain)
        gain.connect(audioContext.destination)
        
        const startTime = audioContext.currentTime
        source.start(startTime)
        backgroundSongStartTimeRef.current = startTime
        backgroundSongSourceRef.current = source
        backgroundSongGainRef.current = gain
        backgroundSongStartedRef.current = true
      } catch (err) {
        console.error('[BackgroundSong] Web Audio API play failed:', err)
      }
      return
    }
    
    // Web: Use HTML5 Audio
    const backgroundSong = backgroundSongRef.current
    if (!backgroundSong) return
    
    // Don't do anything if already playing
    if (!backgroundSong.paused && backgroundSong.currentTime > 0 && !backgroundSong.ended) {
      return // Already playing, don't restart
    }
    
    // Only start if it's actually paused
    if (backgroundSong.paused) {
      try {
        const resumeT = backgroundSongResumeTimeRef.current || 0
        if (resumeT > 0 && resumeT < backgroundSong.duration) {
          backgroundSong.currentTime = resumeT
        }
        backgroundSong.play().catch(() => {
          // Silent fail
        })
        backgroundSongStartedRef.current = true
      } catch {
        // Silent fail
      }
    }
  }

  // Play when the mine sends out the prize nugget (new prize drawn)
  const lastPrizePlayedRef = useRef<number | null>(null)
  useEffect(() => {
    if (typeof s.prize === 'number' && s.ui_phase === 'decide') {
      if (lastPrizePlayedRef.current !== s.prize) {
        playPrizeSound()
        // Background music is now handled by the Android-only effect above
        lastPrizePlayedRef.current = s.prize
        
        // Haptic feedback when prize nugget hits the scale (850ms - slightly before halfway through 2-second audio)
        import('../lib/haptics').then(({ hapticHeavy }) => {
          setTimeout(() => hapticHeavy(), 850);
        }).catch(() => {});
      }
    }
  }, [s.prize, s.ui_phase])

  // short reveal window; tilt only during that window
  const [showLast, setShowLast] = useState(false)

  const CLOCK_ANIMATION_DELAY_MS = 500
  const BEEPS_TRIGGER_SECONDS = 5
  const BEEPS_TRIGGER_OFFSET_MS = -300
  const DISPLAY_BIAS_MS = 800
  const MAX_CLOCK_DRIFT_MS = 1000

  // Trigger clock slide-up animation after prize nugget lands (~850ms after prize appears)
  // Track which rounds have animated using a Set to prevent duplicates
  const [clockHasAnimated, setClockHasAnimated] = useState<Set<number>>(new Set())
  const [clockSlidingDown, setClockSlidingDown] = useState(false)
  const clockTimerRef = useRef<number | null>(null)

  // Delay flag so the final overlay waits a moment (let timeout audio finish)
  const [finishedOverlayReady, setFinishedOverlayReady] = useState(false)
  const finishedTimerRef = useRef<number | null>(null)
  const prevFinishedRef = useRef<boolean>(false)

  useEffect(() => {
    // Clear any pending timer first
    if (clockTimerRef.current !== null) {
      clearTimeout(clockTimerRef.current)
      clockTimerRef.current = null
    }

    // Only animate when we have a prize, we're in decide phase, and this round hasn't animated yet
    if (typeof s.prize === 'number' && s.ui_phase === 'decide' && !clockHasAnimated.has(s.round)) {
      // Wait for prize animation to complete (~820ms) then slide clock
      clockTimerRef.current = window.setTimeout(() => {
        setClockHasAnimated(prev => new Set(prev).add(s.round))
        setClockSlidingDown(false) // Reset slide-down state
        
        // Play clock sound as soon as clock slides up; audio file padding handles alignment
        playRoundClockWithRetry('clock-slide-start', s.round, s.ui_phase, s.prize, 0)
        // Background song is handled by the Android-only effect, don't restart it here

        clockTimerRef.current = null
      }, CLOCK_ANIMATION_DELAY_MS)
    }
    
    return () => {
      if (clockTimerRef.current !== null) {
        clearTimeout(clockTimerRef.current)
        clockTimerRef.current = null
      }
    }
  }, [s.round, s.prize, s.ui_phase, clockHasAnimated])

  // Trigger clock slide-down animation when round ends (during tilt animation)
  useEffect(() => {
    // When showLast becomes true (reveal starts), stop clock audio and slide clock down
    if (showLast && clockHasAnimated.has(s.round) && !clockSlidingDown) {
      // Stop the clock audio before starting the exit animation
      stopClockAudio()
      setClockSlidingDown(true)
    }
    // Reset when reveal ends
    if (!showLast && clockSlidingDown) {
      setClockSlidingDown(false)
    }
  }, [showLast, s.round, clockHasAnimated, clockSlidingDown])
  

  // Stop background song after game over screen appears (let it overlap with GameOver.mp3)
  useEffect(() => {
    if (s.finished) {
      // Calculate delay matching App.tsx navigation delay logic
      const timeoutHoldMs = s.endReason?.startsWith('timeout') ? 1000 : 500
      const tieHoldMs = (s.winner !== 'you' && s.winner !== 'opp') ? 1500 : 0
      const delay = Math.max(timeoutHoldMs, tieHoldMs)
      
      // Keep song playing longer - stop 500ms after navigation to overlap with GameOver.mp3
      // This ensures no gap between background song and game over sound
      const stopDelay = delay + 500
      
      const timer = setTimeout(() => {
        // Android & iOS: Fade out and stop Web Audio API source
        if (useWebAudioAPI) {
          const gain = backgroundSongGainRef.current
          if (gain) {
            try {
              // Fade out over 200ms
              const fadeOutInterval = setInterval(() => {
                if (gain.gain.value > 0.05) {
                  gain.gain.value = Math.max(0, gain.gain.value - 0.1)
                } else {
                  clearInterval(fadeOutInterval)
                  if (backgroundSongSourceRef.current) {
                    try {
                      backgroundSongSourceRef.current.stop()
                      backgroundSongSourceRef.current.disconnect()
                    } catch {}
                    backgroundSongSourceRef.current = null
                  }
                  if (gain) {
                    gain.disconnect()
                    backgroundSongGainRef.current = null
                  }
                }
              }, 20)
              
              // Fallback: stop after fade completes
              setTimeout(() => {
                clearInterval(fadeOutInterval)
                if (backgroundSongSourceRef.current) {
                  try {
                    backgroundSongSourceRef.current.stop()
                    backgroundSongSourceRef.current.disconnect()
                  } catch {}
                  backgroundSongSourceRef.current = null
                }
                if (gain) {
                  gain.disconnect()
                  backgroundSongGainRef.current = null
                }
              }, 300)
            } catch {}
          }
        } else {
          // Web: Fade out HTML5 Audio
          const backgroundSong = backgroundSongRef.current
          if (backgroundSong) {
            try {
              // Fade out over 200ms to smooth the transition
              const fadeOutInterval = setInterval(() => {
                if (backgroundSong.volume > 0.05) {
                  backgroundSong.volume = Math.max(0, backgroundSong.volume - 0.1)
                } else {
                  clearInterval(fadeOutInterval)
                  backgroundSong.pause()
                  backgroundSong.currentTime = 0
                  backgroundSong.volume = 1 // Reset volume for next game
                }
              }, 20)
              
              // Fallback: stop after fade completes
              setTimeout(() => {
                clearInterval(fadeOutInterval)
                try {
                  backgroundSong.pause()
                  backgroundSong.currentTime = 0
                  backgroundSong.volume = 1
                } catch {}
              }, 300)
            } catch {}
          }
        }
        backgroundSongStartedRef.current = false
      }, stopDelay)
      
      return () => clearTimeout(timer)
    }
  }, [s.finished, s.endReason, s.winner])

  // Track/restore background song position across Android WebView suspends
  // Android & iOS: Web Audio API handles suspension automatically, but we resume AudioContext
  // Web: HTML5 Audio needs manual resume
  useEffect(() => {
    if (!useWebAudioAPI) {
      // Web: Handle HTML5 Audio visibility
      const handleVisibilityChange = () => {
        const bg = backgroundSongRef.current
        if (!bg) return
        if (document.visibilityState === 'hidden') {
          try { backgroundSongResumeTimeRef.current = bg.currentTime } catch {}
        } else if (document.visibilityState === 'visible') {
          if (backgroundSongStartedRef.current && shouldPlaySound() && !s.finished && bg.paused) {
            const resumeT = backgroundSongResumeTimeRef.current || 0
            if (bg.currentTime === 0 || (resumeT > 1.0 && bg.currentTime < resumeT - 1.0)) {
              if (resumeT > 0 && resumeT < (bg.duration || Infinity)) {
                try { bg.currentTime = resumeT } catch {}
              }
              if (bg.paused) {
                bg.play().catch(() => {})
              }
            }
          }
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
    // Android & iOS: Just resume AudioContext if suspended
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const audioContext = audioContextRef.current
        if (audioContext && audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [useWebAudioAPI, s.finished])

  // Resume clock ticking after visibility change
  // Android & iOS: Web Audio API handles suspension automatically, just resume AudioContext
  // Web: HTML5 Audio needs manual resume
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      
      // Android & iOS: Resume AudioContext if suspended (Web Audio API handles clock automatically)
      if (useWebAudioAPI) {
        const audioContext = audioContextRef.current
        if (audioContext && audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }
        // If clock source stopped, restart it if it should be playing
        if (!clockSourceRef.current && shouldPlaySound() && !s.finished &&
            s.ui_phase === 'decide' && typeof s.prize === 'number' && !showLast) {
          // Calculate elapsed time to sync the sound
          let elapsedSeconds = 0
          if (s.decisionDeadline && s.decisionSeconds) {
            const roundStart = s.decisionDeadline - s.decisionSeconds * 1000
            const now = Date.now()
            elapsedSeconds = Math.max(0, (now - roundStart) / 1000)
          }
          playRoundClock('visibility-resume', elapsedSeconds)
        }
        return
      }
      
      // Web: Resume HTML5 Audio clock
      const clock = roundClockSoundRef.current
      if (!clock) return
      const shouldBeTicking =
        shouldPlaySound() &&
        !s.finished &&
        s.ui_phase === 'decide' &&
        typeof s.prize === 'number' &&
        !showLast
      if (shouldBeTicking && clock.paused) {
        const resumeT = roundClockResumeTimeRef.current || 0
        if (resumeT > 0 && clock.duration && resumeT < clock.duration - 0.05 && clock.currentTime < resumeT - 0.05) {
          try { clock.currentTime = resumeT } catch {}
        }
        try { void clock.play() } catch {}
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [useWebAudioAPI, s.finished, s.ui_phase, s.prize, showLast])

  // Nudge clock on visibility change (web only - Android & iOS handled above)
  useEffect(() => {
    if (useWebAudioAPI) return // Android & iOS handled in previous effect
    
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return
      
      // Nudge the clock once if it stalled; keep it aligned with elapsed time
      const clock = roundClockSoundRef.current
      if (
        clock &&
        shouldPlaySound() &&
        s.ui_phase === 'decide' &&
        typeof s.prize === 'number' &&
        !s.finished &&
        (clock.paused || clock.ended) &&
        s.decisionDeadline &&
        s.decisionSeconds
      ) {
        const now = Date.now()
        const roundStart = s.decisionDeadline - s.decisionSeconds * 1000
        const elapsed = Math.max(0, (now - roundStart) / 1000)
        try {
          const target = clock.duration
            ? Math.min(clock.duration - 0.2, Math.max(0, elapsed))
            : Math.max(0, elapsed)
          clock.currentTime = target
          void clock.play()
          lastClockNudgeRef.current = now
        } catch {}
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [s.ui_phase, s.prize, s.finished, s.decisionDeadline, s.decisionSeconds])

  // sliding animation state
  const [slidingNuggets, setSlidingNuggets] = useState(false)

  // dropping animation state - nuggets settling onto scale
  const [droppingNuggets, setDroppingNuggets] = useState(false)
  const [hoppingToCenter, setHoppingToCenter] = useState(false)
  
  // Broken scale animation state (for tie games)
  const [brokenScaleFrame, setBrokenScaleFrame] = useState<number | null>(null)

  // PvP/PvE floating animation state
  const [floatingNuggets, setFloatingNuggets] = useState<{you: number | null, opp: number | null}>({you: null, opp: null})
  
  // Handle floating nuggets for both PvP and PvE - show while waiting for opponent
  useEffect(() => {
    let dropTimer: number | undefined
    // If player has moved but opponent hasn't, show floating nugget
    if (s.you_play !== null && s.opp_play === null) {
      setFloatingNuggets({ you: s.you_play, opp: null })
      setDroppingNuggets(false)
    } else if (s.you_play === null && s.opp_play !== null) {
      setFloatingNuggets({ you: null, opp: s.opp_play })
      setDroppingNuggets(false)
    } else if (s.you_play !== null && s.opp_play !== null && s.ui_phase === 'locked') {
      // Both players moved and entered LOCKED phase
      setFloatingNuggets({ you: s.you_play, opp: s.opp_play })
      setDroppingNuggets(false)
      if (s.you_play === s.opp_play) {
        // Tie round: trigger hop-to-center animation
        setHoppingToCenter(true)
        playTieSound()
        // Clear floating nuggets after hop animation completes (700ms)
        dropTimer = window.setTimeout(() => {
          setFloatingNuggets({ you: null, opp: null })
          setHoppingToCenter(false)
        }, 700)
      } else {
        // Non-tie: hold floating for 500ms, then drop
        dropTimer = window.setTimeout(() => setDroppingNuggets(true), 500)
      }
    } else if (s.you_play === null && s.opp_play === null) {
      // Reset for new round
      setFloatingNuggets({ you: null, opp: null })
      setDroppingNuggets(false)
      setHoppingToCenter(false)
    }
    return () => { if (dropTimer) window.clearTimeout(dropTimer) }
  }, [s.you_play, s.opp_play, s.ui_phase])

  // Play drop sound when both nuggets start dropping in LOCKED phase (skip on tie)
  const lastDropKeyRef = useRef<string>('')
  useEffect(() => {
    if (droppingNuggets && s.you_play !== null && s.opp_play !== null && s.ui_phase === 'locked' && s.you_play !== s.opp_play) {
      const key = `${s.you_play}|${s.opp_play}|${s.prize}`
      if (key !== lastDropKeyRef.current) {
        playDropSound()
        lastDropKeyRef.current = key
      }
    }
    if (!droppingNuggets) {
      // reset marker when not dropping
      lastDropKeyRef.current = ''
    }
  }, [droppingNuggets, s.you_play, s.opp_play, s.prize, s.ui_phase])

  // Clear any floating visuals when the game finishes
  useEffect(() => {
    if (s.finished) {
      setFloatingNuggets({ you: null, opp: null })
      setDroppingNuggets(false)
    }
  }, [s.finished])

  // CRITICAL: Clear floating nuggets when a new round starts (new prize drawn)
  // This prevents the first player's nugget from sticking around
  useEffect(() => {
    if (typeof s.prize === 'number' && s.ui_phase === 'decide') {
      setFloatingNuggets({ you: null, opp: null })
      setDroppingNuggets(false)
    }
  }, [s.prize, s.ui_phase])

  // NEW — drive one-shot arc when a new prize appears (even with carry-over)
  const [arcNuggetActive, setArcNuggetActive] = useState(false)
  const [arcKey, setArcKey] = useState(0) // force re-run of CSS keyframes
  const [nowMs, setNowMs] = useState(() => Date.now())

  // Track when we last received a new deadline to handle stale deadlines
  const deadlineReceivedAtRef = useRef<number | null>(null)
  const lastDeadlineRef = useRef<number | null>(null)

  const serverOffsetRef = useRef<number>(0)
  const isRejoiningRef = useRef<boolean>(false)  // Track if we're rejoining mid-round

  // diagnostics: render counter
  const renderCount = useRef(0)
  renderCount.current += 1

  // Reveal type helpers
  const lastOutcome = s.lastReveal?.outcome
  const showLastWin = !!(showLast && (lastOutcome === 'you' || lastOutcome === 'opp'))
  const showLastTie = !!(showLast && lastOutcome === 'tie') // (handy if you want to style ties later)
  const isTieReveal = !!(showLast && s.lastReveal?.outcome === 'tie')
  const decisionExpired =
    !s.finished &&
    s.decisionDeadline !== null &&
    nowMs + serverOffsetRef.current >= s.decisionDeadline
  const canInteract =
    s.prize !== null && !showLast && !decisionExpired && !s.finished

  // Calculate remaining time directly from server deadline
  // When rejoining mid-round, don't apply offset - use deadline directly
  // For new rounds, apply offset to account for network latency
  // Calculate remaining time directly from server deadline (no offset adjustment)
  const remainingMs = !s.finished && s.decisionDeadline !== null
    ? Math.max(0, s.decisionDeadline - (nowMs + (isRejoiningRef.current ? 0 : serverOffsetRef.current)))
    : null

  const remainingSeconds =
    remainingMs !== null
      ? Math.max(0, Math.floor((remainingMs + DISPLAY_BIAS_MS) / 1000))
      : null

  const clockSeconds = remainingSeconds !== null
    ? Math.min(s.decisionSeconds, remainingSeconds)
    : null

  // Clock display: just seconds as number (20, 19, 18...)
  const clockDisplaySeconds =
    clockSeconds !== null && s.prize !== null && !s.finished
      ? clockSeconds
      : null

  const deadlineRemainingMs =
    s.decisionDeadline !== null
      ? Math.max(0, s.decisionDeadline - (nowMs + clockSkewRef.current))
      : null

  useEffect(() => {
    if (!shouldPlaySound()) return
    if (s.finished || s.ui_phase !== 'decide' || typeof s.prize !== 'number') return
    const beepsTriggerMs = BEEPS_TRIGGER_SECONDS * 1000 + BEEPS_TRIGGER_OFFSET_MS
    if (deadlineRemainingMs === null || deadlineRemainingMs > beepsTriggerMs) return
    if (beepsRoundRef.current === s.round) return

    beepsRoundRef.current = s.round
    stopRoundClockAudio()
    playBeeps('clock-5s')
  }, [
    deadlineRemainingMs,
    s.finished,
    s.ui_phase,
    s.prize,
    s.round,
  ])

  useEffect(() => {
    beepsActiveRef.current = false
    beepsRoundRef.current = null
  }, [s.round])

  const clockTimedOut = s.finished && s.endReason?.startsWith('timeout')
  const clockColor = clockTimedOut || decisionExpired ? '#b91c1c' : '#1f2937'

  let finalOverlayMessage: string | null = null
  let finalOverlaySub: string | null = null

  const isTieGame = s.finished && s.winner !== 'you' && s.winner !== 'opp'

  if (s.finished) {
    if (!clockTimedOut && s.winner === 'you') {
      finalOverlayMessage = 'You win!'
      finalOverlaySub = `Final haul: ${s.scores.you} lbs vs ${s.scores.opp} lbs`
    } else if (!clockTimedOut && s.winner === 'opp') {
      finalOverlayMessage = 'Opponent wins!'
      finalOverlaySub = `Final haul: ${s.scores.opp} lbs vs ${s.scores.you} lbs`
    } else if (!clockTimedOut) {
      finalOverlayMessage = 'Draw — the scale breaks!'
      finalOverlaySub = `Both end at ${s.scores.you} lbs`
    }
  }

  // Animate broken scale when tie game is detected
  useEffect(() => {
    if (isTieGame && brokenScaleFrame === null) {
      // Start animation sequence - timing accounts for 300ms crossfade between frames
      setBrokenScaleFrame(1)
      const timer1 = setTimeout(() => {
        setBrokenScaleFrame(2)
      }, 500)  // Increased to 500ms to let crossfade complete
      const timer2 = setTimeout(() => {
        setBrokenScaleFrame(3)
      }, 1000)  // Increased to 1000ms (500ms + 500ms)
      
      return () => {
        clearTimeout(timer1)
        clearTimeout(timer2)
      }
    } else if (!isTieGame && brokenScaleFrame !== null) {
      // Reset animation when game is not a tie
      setBrokenScaleFrame(null)
    }
  }, [isTieGame])

  // Latched tie-freeze flag that stays true from tie reveal start until we explicitly clear
  const tieFrozenRef = useRef<boolean>(false)

  useEffect(() => {
    if (!s.lastReveal || clockTimedOut) return

    // Do not re-enable last-round tokens if we're in decide phase (new prize drawn)
    // (but allow it when game is finished - the last round animation should still play)
    if (s.ui_phase === 'decide') return

    // Additional guard: don't show last if we're already sliding (prevents race conditions)
    if (slidingNuggets) return

    setShowLast(true)
    setSlidingNuggets(false) // Reset sliding state for new reveal

    // start tie freeze when a tie reveal begins
    const isTie = s.lastReveal.outcome === 'tie'
    if (isTie) {
      tieFrozenRef.current = true
      // For ties: don't clear floating nuggets - let hop animation continue
      setDroppingNuggets(false)
    } else {
      // For wins: clear dropping/floating when reveal starts (transition to tilt/slide)
      setDroppingNuggets(false)
      setFloatingNuggets({you: null, opp: null})
    }

    // short flash on tie, normal on win
    const dur = s.lastReveal.outcome === 'tie' ? 400 : 900
    const t = setTimeout(() => setShowLast(false), dur)

    return () => clearTimeout(t)
  }, [
    s.lastReveal?.prize,
    s.lastReveal?.you,
    s.lastReveal?.opp,
    s.lastReveal?.outcome,
    s.prize,
    slidingNuggets,
  ])

  // Hide last-round tokens as soon as a new prize is drawn or the game finishes
  useEffect(() => {
    if (typeof s.prize === 'number' || clockTimedOut) {
      setShowLast(false)
      setSlidingNuggets(false)
    }
  }, [s.prize])

  useEffect(() => {
    if (s.finished) {
      setSlidingNuggets(false)
      // Don't clear showLast here - let the sliding animation complete first
      // showLast will be cleared naturally when the timeout in the showLast effect expires
    }
  }, [s.finished])


  // CRITICAL: Force clear showLast when a new round starts
  // This prevents old lastReveal data from showing when a new prize is drawn
  useEffect(() => {
    if (typeof s.prize === 'number' && showLast) {
      setShowLast(false)
    }
  }, [s.prize, showLast])


  useEffect(() => {
    if (!s.decisionDeadline || s.prize === null || s.finished) {
      setNowMs(Date.now())
      deadlineReceivedAtRef.current = null
      lastDeadlineRef.current = null
      serverOffsetRef.current = 0
      return
    }

    // If this is a new deadline (different from last one), reset our reference time
    if (s.decisionDeadline !== lastDeadlineRef.current) {
      const receivedAt = Date.now()
      deadlineReceivedAtRef.current = receivedAt
      lastDeadlineRef.current = s.decisionDeadline
      if (s.decisionSeconds && s.decisionSeconds > 0) {
        const actualRemaining = s.decisionDeadline - receivedAt
        const desiredRemaining = s.decisionSeconds * 1000
        // Only calculate offset if we're starting a new round (actual remaining is close to desired)
        // When rejoining mid-round, actualRemaining will be much less than desiredRemaining
        // In that case, don't apply offset - use the deadline directly
      if (actualRemaining < desiredRemaining - 5000) {
        // Rejoining mid-round: don't apply offset, use deadline directly
        // The actual remaining time is already correct from the server
        serverOffsetRef.current = 0
        isRejoiningRef.current = true
      } else {
        const targetRemaining = desiredRemaining + CLOCK_ANIMATION_DELAY_MS
        const rawOffset = actualRemaining - targetRemaining
        const clampedOffset = Math.max(-MAX_CLOCK_DRIFT_MS, Math.min(MAX_CLOCK_DRIFT_MS, rawOffset))
        serverOffsetRef.current = clampedOffset
        clockSkewRef.current = Math.max(-MAX_CLOCK_DRIFT_MS, Math.min(MAX_CLOCK_DRIFT_MS, actualRemaining - desiredRemaining))
        isRejoiningRef.current = false
      }
    } else {
      const rawOffset = -CLOCK_ANIMATION_DELAY_MS
      serverOffsetRef.current = Math.max(-MAX_CLOCK_DRIFT_MS, Math.min(MAX_CLOCK_DRIFT_MS, rawOffset))
      clockSkewRef.current = 0
    }
      setNowMs(receivedAt)
    }

    const tick = () => setNowMs(Date.now())
    tick()
    const id = window.setInterval(tick, 200)
    return () => {
      window.clearInterval(id)
    }
  }, [s.decisionDeadline, s.prize, s.finished, s.decisionSeconds])

  useEffect(() => {
    const finished = !!s.finished
    const wasFinished = prevFinishedRef.current
    prevFinishedRef.current = finished

    if (finishedTimerRef.current !== null) {
      window.clearTimeout(finishedTimerRef.current)
      finishedTimerRef.current = null
    }

    if (!finished) {
      setFinishedOverlayReady(false)
      return
    }

    // Only start the delay once per transition into finished
    if (!wasFinished && finished) {
      let delayMs = 1000
      if (s.endReason?.startsWith('timeout')) {
        delayMs = Math.max(1000, CLOCK_ANIMATION_DELAY_MS)
      } else if (s.winner === 'tie' || s.endReason === 'tie') {
        delayMs = Math.max(delayMs, 1500)
      }

      setFinishedOverlayReady(false)
      finishedTimerRef.current = window.setTimeout(() => {
        setFinishedOverlayReady(true)
        finishedTimerRef.current = null
      }, delayMs)
    }
  }, [s.finished, s.endReason, s.winner])

  // Trigger sliding animation after tilt completes
  useEffect(() => {
    if (!showLastWin) return

    // Start sliding after tilt animation completes
    const t = setTimeout(() => {
      setSlidingNuggets(true)
    }, 200) // Small delay after tilt starts

    return () => clearTimeout(t)
  }, [showLastWin])

  // Play tilt/slide sound right when tilt begins (earlier than slide start)
  const tiltSoundKeyRef = useRef<string>('')
  useEffect(() => {
    if (showLast && s.lastReveal && s.lastReveal.outcome !== 'tie') {
      const key = `${s.lastReveal.prize}|${s.lastReveal.you}|${s.lastReveal.opp}|${s.lastReveal.outcome}`
      if (key !== tiltSoundKeyRef.current) {
        playTiltSlideSound()
        tiltSoundKeyRef.current = key
      }
    }
    if (!showLast) {
      // allow future reveals to trigger sound again
      tiltSoundKeyRef.current = ''
    }
  }, [showLast, s.lastReveal?.prize, s.lastReveal?.you, s.lastReveal?.opp, s.lastReveal?.outcome])

  // Call onNextRound after slide animation completes (for wins)
  useEffect(() => {
    if (!slidingNuggets) return

    // Wait for the longest slide animation to complete (1200ms for mine nugget)
    const t = setTimeout(() => {
      onNextRound?.()
    }, 1300) // 1200ms animation + 100ms buffer

    return () => clearTimeout(t)
  }, [slidingNuggets, onNextRound])

  // As soon as the server draws the next prize, stop sliding state so the mine nugget can render
  useEffect(() => {
    if (!slidingNuggets) return
    if (typeof s.prize === 'number') {
      setSlidingNuggets(false)
    }
  }, [slidingNuggets, s.prize])

  // Call onNextRound after a tie reveal (independent of showLast toggling)
  const tieRevealKey = `${s.lastReveal?.prize}|${s.lastReveal?.you}|${s.lastReveal?.opp}|${s.lastReveal?.outcome}`
  const tieSeen = useRef<string>('')

  useEffect(() => {
    if (s.lastReveal?.outcome !== 'tie') return
    if (tieSeen.current === tieRevealKey) return
    tieSeen.current = tieRevealKey

    const t = setTimeout(() => {
      // clear tie freeze just before we ask server to draw next prize
      tieFrozenRef.current = false

      onNextRound?.()
    }, 500) // 400ms tie flash + small buffer

    return () => clearTimeout(t)
  }, [tieRevealKey, onNextRound])

// NEW: snapshot the center value at tie reveal start to prevent number flicker
const tieCenterAtReveal = useRef<number | null>(null)
const tieArtAtReveal = useRef<string | null>(null)

// NEW: snapshot the center total at win reveal start (works for tie-breaker round)
const centerTotalAtWinRevealRef = useRef<number | null>(null)
const centerArtAtWinRevealRef = useRef<string | null>(null)

// Note: onNextRound is now called after slide animation completes above

// ✅ new logic: animate once per new round (pot/prize pair), and
// if we're revealing a WIN, wait until the reveal finishes.
// === Center (mine) animation trigger with queue ===
  const [minePopKey, setMinePopKey] = useState(0)

// A round signature based ONLY on the active prize.
// This avoids remounting the center art during tie (when pot changes)
// which caused image flicker.
const roundSig = typeof s.prize === 'number' ? `p:${s.prize}` : 'p:-1'

// Remember what we already animated, and what’s pending while a WIN reveal shows
const lastAnimatedSig = useRef<string>(roundSig)
const pendingSig = useRef<string | null>(null)

// If the round changes while a WIN reveal is visible, queue it;
// otherwise animate immediately.
useEffect(() => {
  const changed = roundSig !== lastAnimatedSig.current
  if (!changed) return

  if (showLastWin || showLastTie) {
    // Wait until reveal finishes (win or tie)
    pendingSig.current = roundSig
  } else {
    // Animate now
    setMinePopKey(k => k + 1)
    lastAnimatedSig.current = roundSig
    pendingSig.current = null
  }
}, [roundSig, showLastWin, showLastTie])

// The moment a WIN or TIE reveal ends, if we had a pending round, animate it.
useEffect(() => {
  if (!showLastWin && !showLastTie && !s.finished && pendingSig.current && pendingSig.current !== lastAnimatedSig.current) {
    setMinePopKey(k => k + 1)
    lastAnimatedSig.current = pendingSig.current
    pendingSig.current = null
  }
}, [showLastWin, showLastTie, s.finished])

  // local, persistent counters (solve missing/short history)
  const [winsLocal, setWinsLocal] = useState<{ you: number; opp: number }>({ you: 0, opp: 0 })
  const [tieLocal, setTieLocal] = useState(0)

  // detect new-game to hard reset local piles/streak
  const { you: youScore, opp: oppScore } = readScores(s)
  const prevResetKey = useRef<string>('')

  // reset trigger: brand new state (no history, pot 0, scores 0)
  const isFreshGame =
    (youScore === 0 && oppScore === 0) &&
    ((s as any).log?.length ?? (s as any).history?.length ?? 0) === 0 &&
    ((s.pot || 0) === 0)

  useEffect(() => {
    const key = `${youScore}|${oppScore}|${(s as any).log?.length ?? (s as any).history?.length ?? 0}|${s.pot ?? 0}|${s.prize ?? 'x'}`
    if (isFreshGame && key !== prevResetKey.current) {
      setWinsLocal({ you: 0, opp: 0 })
      setTieLocal(0)
    }
    prevResetKey.current = key
  }, [isFreshGame, youScore, oppScore, (s as any).log, (s as any).history, s.pot, s.prize])

  // increment local counters on reveal
  const lastKey = `${s.lastReveal?.you}|${s.lastReveal?.opp}|${s.lastReveal?.outcome}|${s.lastReveal?.prize}`
  const lastSeen = useRef<string>('')
  useEffect(() => {
    if (!s.lastReveal) return
    if (lastSeen.current === lastKey) return
    lastSeen.current = lastKey

    const o = s.lastReveal.outcome
    if (o === 'you') {
      setWinsLocal(w => ({ ...w, you: w.you + 1 }))
      setTieLocal(0)
    } else if (o === 'opp') {
      setWinsLocal(w => ({ ...w, opp: w.opp + 1 }))
      setTieLocal(0)
    } else if (o === 'tie') {
      setTieLocal(t => t + 1)
    }
  }, [lastKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // if tie has been broken (pot==0), ensure center reverts to nugget
  useEffect(() => {
    if ((s.pot || 0) === 0 && s.lastReveal && s.lastReveal.outcome !== 'tie') {
      setTieLocal(0)
    }
  }, [s.pot, s.lastReveal?.outcome])

  // center art (mine): pile only while pot>0; otherwise single nugget
  const tieStreakDisplay = (s.pot || 0) > 0 ? Math.max(1, tieLocal) : 0
  const centerArtHref = tieStreakDisplay > 0 ? (pileFor(tieStreakDisplay) ?? './nugget.png') : './nugget.png'

// Snapshot values ONCE at tie reveal start - don't update during the reveal
  useEffect(() => {
    if (s.lastReveal?.outcome === 'tie' && typeof s.prize === 'number') {
      // Capture the FULL value that's at stake (pot + current prize for tie-breaker)
      // This captures after the prize is drawn, not when tie first occurs
      const tieTotal = (s.pot || 0) + s.prize

      tieCenterAtReveal.current = tieTotal
      tieArtAtReveal.current = centerArtHref
    }
    // Don't clear here - let the sliding animation use the snapshot when tie is broken
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieRevealKey, s.lastReveal?.outcome, s.prize])

  // Snapshot the center total at win reveal start (works for tie-breaker round)
  useEffect(() => {
    if (!s.lastReveal) return
    if (s.lastReveal.outcome === 'you' || s.lastReveal.outcome === 'opp') {
      // at this instant, prize is the current nugget; pot is any carry-over
      const total = (s.pot || 0) + (s.lastReveal.prize || 0)
      centerTotalAtWinRevealRef.current = total
      // art to slide: if there was carry-over we're sliding a pile, else a nugget
      // Use the same logic as the center display to determine pile art
      const tieStreakForSlide = (s.pot || 0) > 0 ? Math.max(1, tieLocal) : 0
      const art = tieStreakForSlide > 0 ? (pileFor(tieStreakForSlide) ?? './nugget.png') : './nugget.png'
      centerArtAtWinRevealRef.current = art
    }
  }, [s.lastReveal?.outcome, s.lastReveal?.prize, s.pot, tieLocal])

  // Clear the snapshot AFTER sliding completes (so slide can show the pile)
  useEffect(() => {
    if (!slidingNuggets && tieCenterAtReveal.current !== null) {
      // Sliding ended, safe to clear

      tieCenterAtReveal.current = null
      tieArtAtReveal.current = null
      centerTotalAtWinRevealRef.current = null
      centerArtAtWinRevealRef.current = null
      tieFrozenRef.current = false  // Also clear freeze so animations can resume
    }
  }, [slidingNuggets])

  // Clear freeze flag when tie reveal ends (not just when sliding ends)
  useEffect(() => {
    if (!showLast && tieFrozenRef.current) {
      tieFrozenRef.current = false
    }
  }, [showLast])


  // During a tie flash, prefer the snapshot; otherwise use live art
  const centerArtDisplayHref = (isTieReveal || tieFrozenRef.current)
    ? (tieArtAtReveal.current ?? centerArtHref)
    : centerArtHref

  // center value = pot + prize; during ties (no prize) show the pot
  // Use stable value during tie animations to prevent flashing
  const centerTotal: number | null = useMemo(() => {
    // During tie reveal, ONLY show the snapshot - ignore all state changes
    if ((isTieReveal || tieFrozenRef.current) && tieCenterAtReveal.current !== null) {
      return tieCenterAtReveal.current
    }
    if (typeof s.prize === 'number') return (s.pot || 0) + s.prize
    if ((s.pot || 0) > 0) return s.pot || 0
    return null
  }, [s.pot, s.prize, isTieReveal])

  // Trigger arc animation only once per prize when carry-over exists
  const shouldStartArc = !showLastWin && !slidingNuggets && typeof s.prize === 'number' && (s.pot || 0) > 0
  const prizeForArc = useRef<number | null>(null)

  useEffect(() => {
    if (shouldStartArc && s.prize !== null && s.prize !== prizeForArc.current) {
      prizeForArc.current = s.prize
      setArcNuggetActive(true)
      setArcKey(k => k + 1)
    }
  }, [shouldStartArc, s.prize])

  // Emergency isolation: hard freeze center during tie to validate root cause
  const freezeDuringTie = true

  // fixed tray slots keep gaps
  const TRAY_SLOTS = [1, 2, 3, 4, 5, 6] as const
  const trayXsFixed = useMemo(() => {
    const span = L.TRAY_SPAN
    const start = viewBox.w / 2 - span / 2
    const step = span / (TRAY_SLOTS.length - 1)
    return TRAY_SLOTS.map((_, i) => Math.round(start + i * step))
  }, [])

  // scale geometry
  const plankRect = {
    x: L.FULCRUM.x - ART.plank.w / 2,
    y: plankCenterY - targetPlankHeight / 2,
    w: ART.plank.w,
    h: targetPlankHeight,
  }
  const baseRect = {
    x: L.FULCRUM.x - ART.base.w / 2,
    y: L.PLANK_Y + 18,
    w: ART.base.w,
    h: ART.base.h,
  }

  const angle = showLastWin ? tiltAngle(s.lastReveal) : 0
  const clockSlideTarget = isIpadLayout
    ? `${Math.max(clockY - 260, 920)}px`
    : (isMobile && !isPortrait ? '815px' : '850px')

  return (
    <svg
      viewBox={`0 0 ${viewBox.w} ${viewBox.h}`}
      preserveAspectRatio={preserveAspect}
      className="w-full h-full"
      style={{ 
        display: 'block', 
        width: '100%', 
        height: '100%', 
        maxHeight: '100%'
      }}
    >
      {/* simple CSS for mine pop */}

      <style>{`
        /* === Mine token animation styles ===
          - Nugget (no tie): dramatic arc + soft settle
          - Pile (tie carryover): quick fade/scale, no arc
        */

        .mine-pop-nugget {
          transform-origin: 960px ${L.PRIZE_Y - 36}px;
          will-change: transform, opacity;
          animation:
            nuggetArc 880ms cubic-bezier(.27,.78,.25,1) forwards,
            nuggetSettle 220ms cubic-bezier(.25,.6,.3,1) 880ms forwards;
          filter: drop-shadow(0 6px 0 rgba(0,0,0,0.15));
        }

        /* smoother, mostly vertical path with *tiny* left bias */
        @keyframes nuggetArc {
          /* start small, below plank */
          0%   { transform: translate( 0px,  90px)   scale(0.00); opacity: 0; }
          /* gentle lift */
          22%  { transform: translate(-4px, -90px)   scale(0.42); opacity: 1; }
          /* mid-rise */
          46%  { transform: translate(-6px, -190px)  scale(0.72); }
          /* near-crest (smooth turn) */
          70%  { transform: translate(-6px, -265px)  scale(0.94); }
          /* approach target with micro overscale */
          92%  { transform: translate(-2px,  -8px)   scale(1.05); }
          /* land centered */
          100% { transform: translate( 0px,   0px)   scale(1.00); opacity: 1; }
        }

        @keyframes nuggetSettle {
          0%   { transform: translate(0, 0)  scale(1.00); }
          50%  { transform: translate(0, 3px) scale(0.986); }
          100% { transform: translate(0, 0)  scale(1.00); }
        }

        /* Tie carryover: show a pile without the arc (no "mine throw" for piles) */
        .mine-pop-pile {
          transform-origin: 960px ${L.PRIZE_Y - 36}px;
          will-change: transform, opacity;
          animation: pileReveal 260ms ease-out forwards;
          filter: drop-shadow(0 6px 0 rgba(0,0,0,0.12));
        }
        @keyframes pileReveal {
          0%   { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1.00); }
        }

        /* stable, no-animation state used during tie flash to avoid flicker */
        .mine-stable { animation: none; opacity: 1; }

        /* === PvP Floating Nuggets Animation === */
        .floating-nugget {
          animation: floatBob 1.5s ease-in-out infinite;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
        }
        
        @keyframes floatBob {
          0%   { transform: translateY(0px); }
          50%  { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }

        /* === Dropping Nuggets Animation === */
        .dropping-nugget {
          animation: dropToScale 500ms cubic-bezier(.4, 0, .2, 1) forwards;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
        }
        
        @keyframes dropToScale {
          0%   { transform: translateY(0px); }
          100% { transform: translateY(${L.PRIZE_Y - 36 - (L.TOKEN_Y - 20)}px); }
        }

        /* === Hop to Center Animation (for ties) === */
        .hopping-to-center-left {
          animation: hopToCenterLeft 700ms cubic-bezier(.4,.2,.3,1) forwards;
          filter: drop-shadow(0 6px 12px rgba(0,0,0,0.3));
        }

        .hopping-to-center-right {
          animation: hopToCenterRight 700ms cubic-bezier(.4,.2,.3,1) forwards;
          filter: drop-shadow(0 6px 12px rgba(0,0,0,0.3));
        }
        /* === Broken Scale Animation Smoothing === */
        .broken-scale-frame {
          animation: crossFadeIn 300ms ease-in-out;
        }

        @keyframes crossFadeIn {
          0% {
            opacity: 0;
            filter: blur(4px);
          }
          100% {
            opacity: 1;
            filter: blur(0px);
          }
        }

        @keyframes hopToCenterLeft {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          50% {
            transform: translate(130px, -60px); /* Halfway to center (260/2), arc up */
            opacity: 1;
          }
          85% {
            transform: translate(260px, 20px); /* At center, down to pile level */
            opacity: 0.8;
          }
          100% {
            transform: translate(260px, 20px);
            opacity: 0;
          }
        }

        @keyframes hopToCenterRight {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          50% {
            transform: translate(-140px, -60px); /* Halfway to center (280/2), arc up */
            opacity: 1;
          }
          85% {
            transform: translate(-280px, 20px); /* At center, down to pile level */
            opacity: 0.8;
          }
          100% {
            transform: translate(-280px, 20px);
            opacity: 0;
          }
        }

        /* FRONT-LAYER arc nugget (with label) */
        .mine-arc-front {
          transform-origin: 960px ${L.PRIZE_Y - 36}px;
          will-change: transform, opacity;
          animation:
            arcFlight 820ms cubic-bezier(.27,.78,.25,1) forwards,
            arcFadeOut 200ms ease-out 780ms forwards; /* overlap a touch for a soft merge */
          filter: drop-shadow(0 6px 0 rgba(0,0,0,0.18));
        }

        @keyframes arcFlight {
          0%   { transform: translate( 0px,  90px)   scale(0.00); opacity: 0; }
          22%  { transform: translate(-4px, -90px)   scale(0.45); opacity: 1; }
          48%  { transform: translate(-6px,-190px)   scale(0.78); }
          72%  { transform: translate(-5px,-260px)   scale(0.98); }
          92%  { transform: translate(-1px, -10px)   scale(1.05); }
          100% { transform: translate( 0px,   0px)   scale(1.00); }
        }

        /* Hide the front nugget as it "merges" into the pile */
        @keyframes arcFadeOut {
          0%   { opacity: 1; }
          100% { opacity: 0; }
        }

        /* Subtle pile settle to sell the merge */
        .pile-settle {
          animation: pileSettle 220ms cubic-bezier(.25,.6,.3,1) 760ms 1 both;
        }
        @keyframes pileSettle {
          0%   { transform: translate(0, 0)      scale(1.00); }
          50%  { transform: translate(0, 3px)    scale(0.986); }
          100% { transform: translate(0, 0)      scale(1.00); }
        }

        /* === Sliding nuggets animation === */

        /* Winner is "you" (left side) - all nuggets slide left */
        .slide-nugget-left.winner-left {
          animation: slideToLeftPile 1000ms cubic-bezier(.4,.0,.2,1) forwards;
        }
        .slide-nugget-right.winner-left {
          animation: slideToLeftPile 800ms cubic-bezier(.4,.0,.2,1) forwards;
        }
        .slide-nugget-mine.winner-left {
          animation: slideToLeftPile 1200ms cubic-bezier(.4,.0,.2,1) forwards;
        }

        /* Winner is "opp" (right side) - all nuggets slide right */
        .slide-nugget-left.winner-right {
          animation: slideToRightPile 1000ms cubic-bezier(.4,.0,.2,1) forwards;
        }
        .slide-nugget-right.winner-right {
          animation: slideToRightPile 800ms cubic-bezier(.4,.0,.2,1) forwards;
        }
        .slide-nugget-mine.winner-right {
          animation: slideToRightPile 1200ms cubic-bezier(.4,.0,.2,1) forwards;
        }

        @keyframes slideToLeftPile {
          0%   { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(-390px, 78px); opacity: 0; }
        }

        @keyframes slideToRightPile {
          0%   { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(390px, 78px); opacity: 0; }
        }

        /* === Clock slide-up animation === */
        /* Clock slides up diagonally from right-to-left at -18° angle */
        /* Starting position: to the right and well below the target (off-screen bottom) */
        /* Moved starting position down another 1000px */
        .clock-hidden {
          transform: translate(120px, 1600px);
          opacity: 0;
        }

        .clock-slide-up {
          animation: clockSlideUp 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        .clock-slide-down {
          animation: clockSlideDown 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        @keyframes clockSlideUp {
          0% {
            transform: translate(120px, 1600px);
            opacity: 0;
          }
          100% {
            transform: translate(150px, ${clockSlideTarget});
            opacity: 1;
          }
        }

        @keyframes clockSlideDown {
          0% {
            transform: translate(150px, ${clockSlideTarget});
            opacity: 1;
          }
          100% {
            transform: translate(120px, 1600px);
            opacity: 0;
          }
        }

      `}</style>

      {/* Background */}
      <image href={effectiveBackgroundHref} x={0} y={0} width={viewBox.w} height={viewBox.h} preserveAspectRatio={backgroundPreserve} />

      {/* Debug overlay: helps diagnose flicker cause visually */}
      {/* Commented out for production
      <g fontSize={14} fill="#000" opacity={0.55} fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
        <rect x={8} y={8} width={500} height={42} rx={8} fill="#fff" opacity={0.65} />
        <text x={16} y={24}>{`r:${renderCount.current} pot:${s.pot} prize:${s.prize ?? '-'} last:${s.lastReveal?.outcome ?? '-'}`}</text>
        <text x={16} y={40}>{`showLast:${String(showLast)} tie:${String(isTieReveal)} snapPot:${tieCenterAtReveal.current ?? '-'}`}</text>
      </g>
      */}

      {/* ===== SCALE (tilt plank + last-round tokens together) ===== */}

      {/* Broken scale animation (for tie games) */}
      {brokenScaleFrame !== null ? (
          <image
            href={`./BrokenScale${brokenScaleFrame}.png`}
            x={0}
            y={0}
            width={viewBox.w}
            height={viewBox.h}
            preserveAspectRatio={backgroundPreserve}
            className="broken-scale-frame"
          />
        ) : (
        <>
          {/* base stays put (rendered first, behind plank) */}
          <image
            href="./scale_base.png"
            x={baseRect.x}
            y={baseRect.y}
            width={baseRect.w}
            height={baseRect.h}
          />

          {/* plank + tilting nuggets */}
          <g transform={`translate(${L.FULCRUM.x}, ${L.FULCRUM.y}) rotate(${angle}) translate(${-L.FULCRUM.x}, ${-L.FULCRUM.y})`}>
            <image href="./scale_plank.png" x={plankRect.x} y={plankRect.y} width={plankRect.w} height={plankRect.h} />
            {showLast && s.lastReveal && !slidingNuggets && s.lastReveal.outcome !== 'tie' && (
              <>
                <Nugget cx={L.PLATE_LEFT_X} cy={L.TOKEN_Y} n={s.lastReveal.you} />
                <Nugget cx={L.PLATE_RIGHT_X} cy={L.TOKEN_Y} n={s.lastReveal.opp} />
              </>
            )}
          </g>
        </>
      )}

      {/* Sliding nuggets (outside the tilted group) */}
      {showLast && s.lastReveal && slidingNuggets && (
        <>
          <g className={`slide-nugget-left ${s.lastReveal.outcome === 'you' ? 'winner-left' : 'winner-right'}`}>
            <Nugget cx={L.PLATE_LEFT_X} cy={L.TOKEN_Y} n={s.lastReveal.you} />
          </g>
          <g className={`slide-nugget-right ${s.lastReveal.outcome === 'you' ? 'winner-left' : 'winner-right'}`}>
            <Nugget cx={L.PLATE_RIGHT_X} cy={L.TOKEN_Y} n={s.lastReveal.opp} />
          </g>
          <g className={`slide-nugget-mine ${s.lastReveal.outcome === 'you' ? 'winner-left' : 'winner-right'}`}>
            <CenteredImage
              href={tieArtAtReveal.current ?? centerArtAtWinRevealRef.current ?? centerArtHref}
              cx={L.FULCRUM.x}
              cy={L.PRIZE_Y - 36}
              w={180}
              h={120}
            />
            <g transform={`translate(${L.FULCRUM.x}, ${L.PRIZE_Y - 36})`}>
              <TokenNumber
                n={
                  tieCenterAtReveal.current  // ← Use tie snapshot FIRST
                  ?? ((s.pot || 0) + (s.lastReveal.prize || 0))
                }
                fontSize={46}
              />
            </g>
          </g>
        </>
      )}

      {/* Floating/Dropping/Hopping nuggets - show while waiting or during LOCKED phase */}
{!s.finished && (!showLast || hoppingToCenter) && !slidingNuggets && (s.ui_phase !== 'reveal' || hoppingToCenter) && (floatingNuggets.you || floatingNuggets.opp) && (
        <>
          {floatingNuggets.you && (
            <g className={
              (droppingNuggets && s.you_play !== s.opp_play) ? "dropping-nugget" :
              (hoppingToCenter && s.you_play === s.opp_play) ? "floating-nugget hopping-to-center-left" :
              "floating-nugget"
            }>
              <Nugget cx={L.PLATE_LEFT_X} cy={L.TOKEN_Y - 20} n={floatingNuggets.you} showNumber={true} />
            </g>
          )}
          {floatingNuggets.opp && (
            <g className={
              (droppingNuggets && s.you_play !== s.opp_play) ? "dropping-nugget" :
              (hoppingToCenter && s.you_play === s.opp_play) ? "floating-nugget hopping-to-center-right" :
              "floating-nugget"
            }>
              {/* Show value when dropping, hopping, or if human already moved */}
              <Nugget 
                cx={L.PLATE_RIGHT_X} 
                cy={L.TOKEN_Y - 20} 
                n={floatingNuggets.opp} 
                showNumber={hoppingToCenter || droppingNuggets || s.you_play !== null} 
                fixedSize={!hoppingToCenter && !droppingNuggets && s.you_play === null} 
              />
            </g>
          )}
        </>
      )}

      {/* ===== MINE center token / tie pile ===== */}
      {/* IMPORTANT: do not show during reveal; this prevents the "next round" nugget
         from appearing while the tilt/slide animation is still on screen */}

      {/* ===== MINE center token / tie pile ===== */}

      {!s.finished && !showLastWin && !slidingNuggets && (typeof centerTotal === 'number' || isTieReveal || tieFrozenRef.current) && (
        <g
          className={`${(isTieReveal || tieFrozenRef.current)
            ? 'mine-stable'
            : (tieStreakDisplay > 0 ? 'mine-pop-pile' : 'mine-pop-nugget')} ${arcNuggetActive ? 'pile-settle' : ''}`}
          key={minePopKey}
        >
          <g transform={`translate(${L.FULCRUM.x}, ${L.PRIZE_Y - 36})`}>
            <image 
              href={freezeDuringTie && (isTieReveal || tieFrozenRef.current) ? (tieArtAtReveal.current ?? centerArtHref) : centerArtDisplayHref}
              x={-90} 
              y={-60} 
              width={180} 
              height={120} 
              preserveAspectRatio="none" 
            />
            {/* Keep showing the center value (total or pot) as you already do */}
            <TokenNumber
              n={freezeDuringTie && (isTieReveal || tieFrozenRef.current) ? (tieCenterAtReveal.current ?? 0) : (centerTotal ?? 0)}
              fontSize={46}
            />
          </g>
        </g>
      )}

      {/* === FRONT-LAYER: arc nugget (ABOVE pile), auto-fades at landing === */}
      {arcNuggetActive && !showLastWin && !slidingNuggets && typeof s.prize === 'number' && (s.pot || 0) > 0 && (
        <g
          className="mine-arc-front"
          key={`arc-${arcKey}`}
          onAnimationEnd={() => setArcNuggetActive(false)}
        >
          <g transform={`translate(${L.FULCRUM.x}, ${L.PRIZE_Y - 36})`}>
            <image href="./nugget.png" x={-90} y={-60} width={180} height={120} preserveAspectRatio="none" />
            {/* show the *new prize's* number on the flying nugget */}
            <TokenNumber n={s.prize} fontSize={46} />
          </g>
        </g>
      )}

      {/* ===== Persistent winner piles under signs (grow 3→6→9) ===== */}
      {pileFor(winsLocal.you) && (
        <CenteredImage href={pileFor(winsLocal.you)!} cx={L.PILE_LEFT.x}  cy={pileLeftY}  w={210} h={132} />
      )}
      {pileFor(winsLocal.opp) && (
        <CenteredImage href={pileFor(winsLocal.opp)!} cx={L.PILE_RIGHT.x} cy={pileRightY} w={210} h={132} />
      )}

      {/* Clock with slide-up animation - rendered AFTER piles so it appears on top */}
      {(clockDisplaySeconds !== null || clockTimedOut || clockSlidingDown) && (
        <g 
          transform={`translate(${L.CLOCK_FACE.x}, ${clockY})`}
          className={
            clockSlidingDown 
              ? 'clock-slide-down' 
              : clockHasAnimated.has(s.round) 
              ? 'clock-slide-up' 
              : 'clock-hidden'
          }
        >
          {/* CLOCK FACE (apply image offsets here) */}
          <image
            href="./Clock.png"
            x={-CLOCK.size / 2 + CLOCK.imgDx}
            y={-CLOCK.size / 2 + CLOCK.imgDy}
            width={CLOCK.size}
            height={CLOCK.size}
            preserveAspectRatio="xMidYMid"
          />

          {/* DEBUG: crosshair & guide ring — enable once to center visually */}
          {false && (
            <>
              <circle cx="0" cy="0" r={CLOCK.size * 0.40} fill="none" stroke="red" strokeWidth="2"/>
              <line x1="-15" y1="0" x2="15" y2="0" stroke="red" strokeWidth="2"/>
              <line x1="0" y1="-15" x2="0" y2="15" stroke="red" strokeWidth="2"/>
            </>
          )}

          {/* NUMBER (translate the group; ignore baselines) */}
          {clockDisplaySeconds !== null && (
            <g transform={`translate(${CLOCK.textDx}, ${CLOCK.textDy})`}>
              <text
                x={0}
                y={0}
                textAnchor="middle"
                fontFamily="'Fira Code', ui-monospace, monospace"
                fontWeight={clockDisplaySeconds <= 5 ? 900 : 700}
                fontSize={clockDisplaySeconds <= 5 ? 110 : 78}
                fill={clockDisplaySeconds <= 5 ? '#8B0000' : clockColor}
              >
                {clockDisplaySeconds}
              </text>
            </g>
          )}

          {clockTimedOut && (
            <g transform={`translate(${CLOCK.textDx}, ${CLOCK.textDy})`}>
              <text
                x={0}
                y={0}
                textAnchor="middle"
                fontFamily="'Fira Code', ui-monospace, monospace"
                fontWeight={700}
                fontSize={44}
                fill={clockColor}
              >
                TIME!
              </text>
            </g>
          )}
        </g>
      )}

      {/* ===== Player Avatars and Usernames ===== */}
      {/* Left player avatar and username - aligned with background avatar position */}
      <foreignObject x={330 - 100} y={avatarY} width={200} height={200}>
        <div className="flex flex-col items-center">
          <Avatar name={playerUsername} size={200} />
        </div>
      </foreignObject>
      <g fontFamily="system-ui, ui-sans-serif" textAnchor="middle" fill="#f4f4f5" stroke="#3e2615" strokeWidth={3} paintOrder="stroke">
        <text x={330} y={textY} fontSize={34} fontWeight={800}>
          {playerUsername}
        </text>
        
      </g>
      
      {/* Right opponent avatar and username - only show if opponent has joined */}
      {opponentUsername && opponentUsername !== 'Opponent' && opponentUsername !== 'Waiting for opponent...' && (
        <>
          <foreignObject x={1590 - 100} y={avatarY} width={200} height={200}>
            <div className="flex flex-col items-center">
              <Avatar name={opponentUsername} size={200} />
            </div>
          </foreignObject>
          <g fontFamily="system-ui, ui-sans-serif" textAnchor="middle" fill="#f4f4f5" stroke="#3e2615" strokeWidth={3} paintOrder="stroke">
            <text x={1590} y={textY} fontSize={34} fontWeight={800}>
              {opponentUsername}{(["Lump", "Ferris", "Magnus"].includes(opponentUsername)) && " [AI]"}
            </text>
          </g>
        </>
      )}
      {/* Show waiting message and countdown if opponent hasn't joined yet */}
      {(!opponentUsername || opponentUsername === 'Opponent' || opponentUsername === 'Waiting for opponent...') && (
        <GracePeriodCountdown opponentUsername={opponentUsername} textY={textY} />
      )}

      {/* ===== Sign text (label + score), moved down & toward center ===== */}
      <g fontFamily="system-ui, ui-sans-serif, -apple-system, Segoe UI" textAnchor="middle" fill="#3e2615">
        {/* Left sign (You) */}
        <text x={L.SIGN_LEFT.x} y={L.SIGN_LEFT.y - 20} fontSize={44} fontWeight={800}>
          You:
        </text>
        <text x={L.SIGN_LEFT.x} y={L.SIGN_LEFT.y + 56} fontSize={78} fontWeight={900}>
          {youScore} lbs
        </text>

        {/* Right sign (Them) */}
        <text x={L.SIGN_RIGHT.x} y={L.SIGN_RIGHT.y - 20} fontSize={44} fontWeight={800}>
          Them:
        </text>
        <text x={L.SIGN_RIGHT.x} y={L.SIGN_RIGHT.y + 56} fontSize={78} fontWeight={900}>
          {oppScore} lbs
        </text>
      </g>

      {s.finished && finalOverlayMessage && finishedOverlayReady && (
        <g transform={`translate(${viewBox.w / 2}, 170)`} textAnchor="middle">
          <rect x={-320} y={-70} width={640} height={140} rx={28} fill="rgba(255,248,237,0.92)" stroke="#c2410c" strokeWidth={6} />
          <text y={-8} fontSize={58} fontWeight={900} fill="#7c2d12" fontFamily="'Luckiest Guy', system-ui">
            {finalOverlayMessage}
          </text>
          {finalOverlaySub && (
            <text y={44} fontSize={28} fontFamily="system-ui, ui-sans-serif" fill="#7c2d12">
              {finalOverlaySub}
            </text>
          )}
        </g>
      )}

      {/* ===== Clickable remaining nuggets tray (fixed slots keep gaps) ===== */}
      {!s.finished && (
        <g>
          {[1,2,3,4,5,6].map((slotWeight, i) =>
            s.yourRemaining.includes(slotWeight) ? (
              <g
                key={slotWeight}
                style={{
                  cursor: canInteract ? 'pointer' : 'default',
                  pointerEvents: canInteract ? 'auto' : 'none',
                }}
                onClick={() => { if (s.you_play === null) playChooseSound(); onPlay(slotWeight) }}
              >
                <Nugget cx={trayXsFixed[i]} cy={trayY} n={slotWeight} />
              </g>
            ) : (
              <g key={slotWeight} />
            )
          )}
        </g>
      )}
    </svg>
  )
}
